import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Joyride, STATUS, ACTIONS } from 'react-joyride';
import { AuthContext } from '../components/Auth/AuthContext';

const BUDGETING_TOUR_KEY = 'demoTourShown';
const STOCKS_TOUR_KEY = 'demoStocksTourShown';
const ADD_TRANSACTION_TOUR_KEY = 'demoAddTransactionTourShown';
const PROFILE_TOUR_KEY = 'demoProfileTourShown';

const PORTFOLIO_ROUTES = [
  '/portfolio', '/positions', '/bonds', '/cash-flows',
  '/brokers', '/stock-transactions', '/add-stock-transaction',
  '/import-portfolio', '/export-portfolio', '/stocks',
];

const NEUTRAL_ROUTES = ['/settings'];

const seen = (key) => !!localStorage.getItem(key);

const addTransactionSteps = [
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Adding a Transaction',
    content: 'This form lets you log any financial event — an expense, income, or investment. All fields work the same way regardless of type.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Type & Category',
    content: 'First pick the type: Expense, Income, or Investment. Then pick a category from the list — categories are colour-coded and used to power the spending charts on the Dashboard.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Date, Vendor & Amount',
    content: 'Date is when the transaction happened. Vendor is the shop, company, or person you paid (or received from). Amount is the value in your chosen currency.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Note (Optional)',
    content: 'Use the Note field to add context — "birthday gift", "Q2 client invoice", "AAPL top-up". Notes are searchable on the transaction list pages.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Recurring Payments',
    content: 'Check "Make recurring" to turn this into a scheduled payment — Netflix, rent, a loan instalment, etc. Choose how often it repeats (daily / weekly / monthly / yearly) and whether it runs forever or stops after a set number of payments.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Installments',
    content: 'Enabling installments lets you enter the total purchase price and split it equally across the recurring payments. OrganizeFlow calculates the per-payment amount automatically.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Demo Limit',
    content: 'In demo mode you can add up to 3 transactions to try the form. Note: demo statistics are pre-loaded and won\'t update when you add transactions — sign up to track your real finances.',
  },
];

const profileSteps = [
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Your Profile',
    content: 'Your profile stores the settings that personalise OrganizeFlow. Changes here apply immediately across the whole app.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Display Name',
    content: 'This is the name other group members will see when you share expenses together. It does not have to match your real name.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Preferred Currency',
    content: 'All amounts in the app — totals, charts, stats — are shown in this currency. You can hold transactions in any currency; OrganizeFlow converts them automatically using live exchange rates.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Groups & Shared Expenses',
    content: 'Groups let you track shared finances with other people — a household, a trip, a team. Each group has its own transaction history and categories, separate from your personal finances.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Group Invitations',
    content: 'When someone invites you to a group, the invitation appears here. Accept to gain access to that group\'s shared expenses, or decline to ignore it. The group owner\'s email must match the email on their profile.',
  },
];

const budgetingSteps = [
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Welcome to OrganizeFlow!',
    content: 'This is a live demo — explore a personal finance and investment tracking app with pre-loaded sample data. Take a quick tour first, then explore freely.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Dashboard',
    content: 'The Dashboard gives you a complete financial overview: income vs expenses, spending by category, top vendors, and upcoming recurring payments — all at a glance.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Expenses, Income & Investments',
    content: 'Separate pages for each transaction type. Filter by date range, search by vendor, and see a full history. You can add up to 3 demo transactions to try the form.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Recurring Payments',
    content: 'Track subscriptions and recurring bills. OrganizeFlow auto-calculates your monthly commitment and shows what\'s due soon — Netflix, electricity, rent, etc.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Categories',
    content: 'Create custom spending categories with colors. Every transaction is tagged, powering the charts and spending breakdowns on the Dashboard.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Two Modes: Budgeting & Stocks',
    content: 'Use the mode selector in the top navigation bar to switch between Budgeting (income/expenses) and Stocks mode (investment portfolio with FIFO positions, unrealized P&L, and price history charts).',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: "You're All Set!",
    content: 'Feel free to explore every page with the sample data. You can add up to 3 transactions to try the form — note that the dashboard statistics are pre-loaded and won\'t update in demo mode. Sign up for free to track your real finances.',
  },
];

const stocksSteps = [
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Welcome to Stocks Mode!',
    content: 'Track your investment portfolio with FIFO cost averaging, live prices, and unrealized P&L — all calculated automatically from your buy/sell history.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Portfolio Overview',
    content: 'The Portfolio page shows your total invested value, current market value, overall P&L, and an allocation breakdown by broker and position.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Positions',
    content: 'Positions shows your FIFO-calculated average cost, current quantity, and unrealized P&L % for each ticker. Click any ticker for a full price chart.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Stock Activity',
    content: 'Stock Activity lists every buy and sell transaction by broker. You can add up to 2 demo orders to try the form — just like adding a real trade.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Stock Detail',
    content: 'Click any ticker in Positions to see a 1-year price history chart, company fundamentals (P/E, EPS, dividend yield), and recent news headlines.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Switch to Budgeting Mode',
    content: 'Use the mode selector in the top navigation bar to switch back to Budgeting mode — track income, expenses, and recurring payments alongside your investment portfolio.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Portfolio Overview',
    content: 'This page shows your total invested capital, current market value, unrealized profit/loss, and a value history chart — all in your preferred currency.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Value History Chart',
    content: 'The chart plots your portfolio\'s market value (purple line) vs. total cost basis (grey dashed line) over time. Use the range buttons (5D, 1M, 1Y…) to zoom in or out.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Backfill History',
    content: 'The "Backfill History" button rebuilds your portfolio history from scratch — it fetches ECB FX rates, refreshes stock prices from Yahoo Finance, and generates a daily value snapshot for every past date. Run it once after adding your holdings.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'By Broker & By Currency',
    content: 'The tables below the chart break down your portfolio by broker and by currency — useful if you hold assets in multiple accounts or currencies. Values are converted to your preferred display currency.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: "Ready to Invest Smarter?",
    content: 'Sign up for free and start tracking your real portfolio. All your data stays private — no ads, no data selling, just clean finance tracking.',
  },
];

const joyrideStyles = {
  options: {
    primaryColor: '#1976d2',
    zIndex: 10000,
    backgroundColor: '#fff',
    textColor: '#333',
    overlayColor: 'rgba(0,0,0,0.45)',
  },
  tooltip: { borderRadius: 8, padding: '20px 24px', maxWidth: 380 },
  tooltipTitle: { fontSize: '1rem', fontWeight: 600, marginBottom: 8, color: '#1976d2' },
  tooltipContent: { fontSize: '0.875rem', lineHeight: 1.6 },
  buttonNext: { backgroundColor: '#1976d2', borderRadius: 6, padding: '8px 18px', fontSize: '0.8rem' },
  buttonBack: { color: '#1976d2', fontSize: '0.8rem' },
  buttonSkip: { color: '#888', fontSize: '0.8rem' },
};

const DemoTour = () => {
  const { isDemoMode } = useContext(AuthContext);
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [tourKey, setTourKey] = useState(0);
  const [seenVersion, setSeenVersion] = useState(0);

  // runRef guards against handleCallback double-fire (Joyride can call it more than once).
  // activeTourKeyRef captures which tour was active when run=true, so handleCallback always
  // marks the correct key even if activeKey has already changed in a new render by the time
  // Joyride fires its second callback event.
  const runRef = useRef(false);
  const activeTourKeyRef = useRef(null);

  const markSeen = useCallback((key) => {
    localStorage.setItem(key, 'true');
    setSeenVersion(v => v + 1);
  }, []);

  const { activeSteps, activeKey } = useMemo(() => {
    const path = location.pathname;

    if (NEUTRAL_ROUTES.some(r => path.startsWith(r))) return {};

    if (PORTFOLIO_ROUTES.some(r => path.startsWith(r))) {
      if (!seen(STOCKS_TOUR_KEY)) return { activeSteps: stocksSteps, activeKey: STOCKS_TOUR_KEY };
      return {};
    }

    if (path.startsWith('/add-transaction') && !seen(ADD_TRANSACTION_TOUR_KEY))
      return { activeSteps: addTransactionSteps, activeKey: ADD_TRANSACTION_TOUR_KEY };

    const appMode = localStorage.getItem('appMode') || 'budgeting';
    if (path.startsWith('/profile') && appMode !== 'stocks' && !seen(PROFILE_TOUR_KEY))
      return { activeSteps: profileSteps, activeKey: PROFILE_TOUR_KEY };

    if (!seen(BUDGETING_TOUR_KEY))
      return { activeSteps: budgetingSteps, activeKey: BUDGETING_TOUR_KEY };

    return {};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, seenVersion]);

  // Keep runRef in sync and capture the active key the moment a tour starts.
  useEffect(() => {
    runRef.current = run;
    if (run && activeKey) activeTourKeyRef.current = activeKey;
  }, [run, activeKey]);

  // Navigation-away: if activeKey changes while a tour is running, mark the old tour seen and stop.
  const prevKeyRef = useRef(undefined);
  useEffect(() => {
    const prev = prevKeyRef.current;
    prevKeyRef.current = activeKey;
    if (prev && prev !== activeKey && runRef.current) {
      runRef.current = false;
      setRun(false);
      markSeen(prev);
    }
  }, [activeKey, markSeen]);

  // Auto-launch: start a tour whenever one becomes active and nothing is already running.
  // Write to localStorage before setRun so a hard reload (mode switch) never repeats the tour.
  // We deliberately do NOT bump seenVersion here — that would cause useMemo to return {}
  // and hide the tour before it starts. seenVersion only bumps in markSeen (tour end/skip).
  useEffect(() => {
    if (!isDemoMode || run || !activeKey) return;
    const keyToLaunch = activeKey;
    const timer = setTimeout(() => {
      localStorage.setItem(keyToLaunch, 'true');
      setRun(true);
    }, 800);
    return () => clearTimeout(timer);
  }, [isDemoMode, activeKey, run, seenVersion]);

  useEffect(() => {
    const handleReplay = () => { setTourKey(k => k + 1); setRun(true); };
    window.addEventListener('demo:replayTour', handleReplay);
    return () => window.removeEventListener('demo:replayTour', handleReplay);
  }, []);

  // useCallback so Joyride always holds the same function reference.
  // Reads from refs to avoid stale closures and prevent double-fire.
  const handleCallback = useCallback(({ status, action }) => {
    const finished = [STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE;
    if (finished && runRef.current) {
      runRef.current = false;
      setRun(false);
      const key = activeTourKeyRef.current;
      activeTourKeyRef.current = null;
      if (key) markSeen(key);
    }
  }, [markSeen]);

  if (!isDemoMode || !activeSteps) return null;

  return (
    <Joyride
      key={`${tourKey}-${activeKey}`}
      steps={activeSteps}
      run={run}
      continuous
      showSkipButton
      showProgress
      disableScrolling
      styles={joyrideStyles}
      locale={{ back: '← Back', close: 'Close', last: 'Start Exploring!', next: 'Next →', skip: 'Skip Tour' }}
      callback={handleCallback}
    />
  );
};

export default DemoTour;
